import { useState, useEffect } from 'react';
import { X, Search, Loader } from 'lucide-react';
import { motion } from 'framer-motion';

interface EditCampaignReleaseModalProps {
  releaseId: string;
  releaseName: string | null;
  currentCampaignId: string | null;
  currentCampaignName: string | null;
  onClose: () => void;
  onSave: (campaignId: string, campaignName: string) => Promise<void>;
}

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  status: string;
  effective_status: string;
  objective: string;
}

const REPORTING_SUPABASE_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY = 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

export function EditCampaignReleaseModal({
  releaseName,
  currentCampaignId,
  currentCampaignName,
  onClose,
  onSave
}: EditCampaignReleaseModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    currentCampaignId ? {
      campaign_id: currentCampaignId,
      campaign_name: currentCampaignName || '',
      status: 'ACTIVE',
      effective_status: 'ACTIVE',
      objective: ''
    } : null
  );

  const searchCampaigns = async (query: string) => {
    if (query.length < 2) {
      setCampaigns([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/search-meta-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          search_pattern: query,
          account_id: '626419741407621'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error searching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        searchCampaigns(searchQuery);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSave = async () => {
    if (!selectedCampaign) return;
    
    setSaving(true);
    try {
      await onSave(selectedCampaign.campaign_id, selectedCampaign.campaign_name);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white">
            Meta Campaign for "{releaseName}"
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          {/* Current Campaign */}
          {currentCampaignId && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Current Campaign</p>
              <p className="font-medium text-blue-900 dark:text-blue-100">{currentCampaignName || currentCampaignId}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">ID: {currentCampaignId}</p>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : campaigns.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {campaigns.map((campaign) => (
                <button
                  key={campaign.campaign_id}
                  onClick={() => setSelectedCampaign(campaign)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedCampaign?.campaign_id === campaign.campaign_id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <p className="font-medium dark:text-white">{campaign.campaign_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {campaign.objective} • {campaign.effective_status}
                  </p>
                </button>
              ))}
            </div>
          ) : searchQuery.length >= 2 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No campaigns found
            </p>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              Type at least 2 characters to search
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedCampaign || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </motion.div>
    </div>
  );
}
